import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Heart, 
  Clock, 
  Users, 
  Coffee, 
  Music, 
  Target,
  AlertTriangle,
  MessageCircle,
  ExternalLink,
  BookOpen,
  Sparkles,
  ArrowRight,
  Calendar,
  TrendingDown,
  Search,
  Home
} from "lucide-react";
import { Link } from "react-router-dom";

// Studyverse images
import studyverseLibraryRoom from "@/assets/studyverse/studyverse-library-room.jpeg";
import studyverseCherryBlossom from "@/assets/studyverse/studyverse-cherry-blossom.jpg";
import studyverseRainyLibrary from "@/assets/studyverse/studyverse-rainy-library.jpg";
import studyversePublicRooms from "@/assets/studyverse/studyverse-public-rooms.jpg";
import studyverseMobileApp from "@/assets/studyverse/studyverse-mobile-app.jpeg";
import studyverseCozyFireplace from "@/assets/studyverse/studyverse-cozy-fireplace.jpg";

const StudyVerseHistory = () => {

  const features = [
    { icon: Home, text: "Virtual study rooms that felt cozy, not corporate" },
    { icon: Clock, text: "Pomodoro timers, task lists, and streak tracking" },
    { icon: Coffee, text: "Ambient backgrounds like libraries and cafés" },
    { icon: Music, text: "Soft music and white noise built directly into rooms" },
    { icon: Users, text: "Silent co-working with strangers who felt familiar" },
    { icon: Heart, text: "Small communities that formed organically over time" },
  ];

  const alternatives = [
    { name: "Focusmate", url: "/online-study/focusmate/", description: "Virtual coworking sessions", internal: true },
    { name: "StudyStream", url: "/online-study/studystream/", description: "Live study rooms", internal: true },
    { name: "Study Together", url: "/online-study/studytogether/", description: "Discord-based community", internal: true },
    { name: "Fiveable", url: "/online-study/fiveable/", description: "AP exam prep platform", internal: true },
    { name: "Quizlet", url: "/online-study/quizlet/", description: "Flashcard-based learning", internal: true },
  ];

  const cswFeatures = [
    "Free virtual study rooms",
    "No time limits",
    "No paywalls",
    "Integrated music and media",
    "Focus timers and task tools",
    "Study + work hybrid spaces",
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/studyverse/history/"
    },
    "headline": "It's Been Two Years Since Studyverse Went Offline – Here's What I Miss About It",
    "alternativeHeadline": "Studyverse: A Look Back at Its Rise, Fall, and Successor Platforms",
    "description": "This article recounts the rise and fall of Studyverse, a virtual study hall launched in late 2020. It highlights the platform's key features—immersive study rooms, built‑in productivity tools, and a supportive community of students—reflects on the sudden shutdown in mid‑2024 without warning, explores users' emotional reactions and reunion spaces, and examines alternative virtual study platforms like LifeAt, StudyStream, Study Together, and CSW (Chill Study Work).",
    "keywords": "Studyverse, virtual study platform, online workspace, virtual study hall, immersive study rooms, productivity tools, study communities, LifeAt, StudyStream, Study Together, CSW, Chill Study Work, shutdown, alternatives",
    "articleSection": "Technology, Education",
    "genre": ["Technology", "Education", "Blog"],
    "inLanguage": "en",
    "wordCount": 2000,
    "about": ["Studyverse", "virtual study platforms", "online study tools"],
    "author": {
      "@type": "Organization",
      "name": "CSW Team",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Chill Study Work",
      "url": "https://csw.live",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/og-image.png",
        "width": 1200,
        "height": 630
      }
    },
    "datePublished": "2024-08-01",
    "dateModified": "2025-01-11",
    "image": {
      "@type": "ImageObject",
      "url": "https://csw.live/og-image.png",
      "width": 1200,
      "height": 630
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Studyverse History - The Journey of Virtual Study Spaces"
        description="A retrospective on Studyverse - what it was, why it mattered, how it disappeared, and where former users found community again. Discover the legacy of virtual study halls."
        keywords="studyverse history, studyverse story, studyverse shutdown, virtual study room history, online study community, studyverse alternative"
        canonicalUrl="/studyverse/history/"
        metaImageDescription="studyverse"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.15),transparent_50%)]" />
          
          <div className="container mx-auto px-4 relative z-10">
            {/* Breadcrumb */}
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li>/</li>
                <li><Link to="/studyverse/" className="hover:text-primary transition-colors">Studyverse</Link></li>
                <li>/</li>
                <li className="text-foreground">History</li>
              </ol>
            </nav>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Calendar className="w-4 h-4" />
                A Retrospective • 2020-2024
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                The Studyverse Story
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                It's been two years since Studyverse went offline, and I still catch myself missing it more than I expected.
              </p>
              
              <p className="text-lg text-muted-foreground/80 max-w-2xl mx-auto">
                Not just as a study tool — but as a <span className="text-foreground font-medium">place</span>.
              </p>
            </div>

            {/* Hero Image */}
            <div className="mt-12 max-w-4xl mx-auto">
              <img 
                src={studyverseLibraryRoom} 
                alt="Studyverse virtual library study room with timer and task list" 
                className="w-full rounded-2xl shadow-2xl border border-border/30"
                loading="eager"
              />
              <p className="text-center text-sm text-muted-foreground mt-3 italic">
                A classic Studyverse study room — cozy virtual libraries with timers, tasks, and ambient music.
              </p>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-muted-foreground">
                    Studyverse wasn't perfect, but it created something rare on the internet: a shared sense of <span className="text-foreground font-semibold">quiet presence</span>. You weren't alone, but you weren't distracted either. You showed up, sat down, and worked — together.
                  </p>
                  <p className="text-lg leading-relaxed text-muted-foreground mt-4">
                    This is a look back at what Studyverse was, why it mattered, how it disappeared, and where people like me ended up after it was gone.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What Studyverse Was */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">What Studyverse Was</h2>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Studyverse launched around 2020–2021 as a virtual study hall designed primarily for students who wanted accountability, calm, and community. The founder, Eric Drymer, positioned it as a "virtual study hall for Gen Z" — a place where studying online didn't feel isolating.
                </p>
                
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  It grew quickly during the remote-learning boom. According to public interviews and startup profiles, Studyverse raised roughly <span className="text-foreground font-semibold">$1.8M in funding</span> and reached hundreds of thousands of monthly users at its peak.
                </p>

                <div className="flex flex-wrap gap-3 mb-8">
                  <a 
                    href="https://www.linkedin.com/in/ericdrymer" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Eric Drymer on LinkedIn
                  </a>
                  <a 
                    href="https://www.crunchbase.com/organization/studyverse" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Crunchbase Profile
                  </a>
                </div>

                <p className="text-xl text-foreground font-medium italic border-l-4 border-primary pl-6">
                  But numbers don't explain why people loved it.
                </p>

                {/* Public Rooms Screenshot */}
                <div className="mt-8">
                  <img 
                    src={studyversePublicRooms} 
                    alt="Studyverse public rooms browser showing various themed study rooms" 
                    className="w-full rounded-xl shadow-lg border border-border/30"
                    loading="lazy"
                  />
                  <p className="text-center text-sm text-muted-foreground mt-3 italic">
                    The Studyverse room browser — users could join themed public rooms or create their own.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why People Loved It */}
        <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Why People Loved It</h2>
              </div>
              
              <p className="text-muted-foreground text-lg mb-8">
                Studyverse combined several things into one experience:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {features.map((feature, index) => (
                  <Card key={index} className="border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                        <feature.icon className="w-5 h-5 text-primary" />
                      </div>
                      <p className="text-foreground">{feature.text}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Cherry Blossom Room Image */}
              <div className="mb-10">
                <img 
                  src={studyverseCherryBlossom} 
                  alt="Studyverse anime-style cherry blossom study room with group timer and chat" 
                  className="w-full rounded-xl shadow-lg border border-border/30"
                  loading="lazy"
                />
                <p className="text-center text-sm text-muted-foreground mt-3 italic">
                  Studyverse's anime-inspired cherry blossom room — complete with group timers and chat.
                </p>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  You didn't need to talk. You didn't need to perform. You just needed to <span className="text-foreground font-medium">show up</span>.
                </p>
                <p>
                  Many users logged in daily and studied with the same group of strangers for months. Some rooms became unofficial "homes" — people knew each other's usernames, habits, even exam schedules.
                </p>
                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      And importantly: it was free.
                    </p>
                    <p className="text-muted-foreground mt-2">
                      That made it accessible, but it also turned out to be part of the problem.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* When Studyverse Went Down */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-destructive/10">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">When Studyverse Went Down</h2>
              </div>

              <div className="relative pl-8 border-l-2 border-destructive/30 space-y-8">
                <div className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 rounded-full bg-destructive" />
                  <p className="text-lg font-medium text-foreground">Mid-2024</p>
                  <p className="text-muted-foreground mt-2">
                    Studyverse went offline without warning.
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 rounded-full bg-destructive/60" />
                  <p className="text-muted-foreground">
                    No banner. No email. No countdown. No archive.
                  </p>
                </div>
                
                <div className="relative">
                  <div className="absolute -left-[41px] w-4 h-4 rounded-full bg-destructive/40" />
                  <p className="text-muted-foreground">
                    One day it simply stopped loading.
                  </p>
                </div>
              </div>

              <div className="mt-10 space-y-6 text-lg text-muted-foreground">
                <p>
                  People assumed it was maintenance. Then hours passed. Then days.
                </p>
                <p>
                  Eventually, users confirmed through Discord and Reddit that the platform was permanently shut down. No official public statement followed.
                </p>

                <div className="flex flex-wrap gap-3">
                  <Link 
                    to="/blog/how-to-stop-procrastination/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    How to Stop Procrastination
                  </Link>
                  <Link 
                    to="/blog/science-behind-study-with-me/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Science Behind Study With Me
                  </Link>
                </div>

                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      This silence is what hurt the most.
                    </p>
                    <p className="text-muted-foreground mt-2">
                      People didn't get to say goodbye. They didn't get to screenshot their streaks. They didn't get to exchange contact info with the people they studied with every day.
                    </p>
                  </CardContent>
                </Card>

                {/* Rainy Library Image */}
                <div className="mt-8">
                  <img 
                    src={studyverseRainyLibrary} 
                    alt="Studyverse rainy library room with cozy fireplace and group chat" 
                    className="w-full rounded-xl shadow-lg border border-border/30"
                    loading="lazy"
                  />
                  <p className="text-center text-sm text-muted-foreground mt-3 italic">
                    The cozy rainy library — one of Studyverse's most beloved ambient rooms.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Reaction */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <MessageCircle className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">The Reaction</h2>
                <span className="text-sm text-muted-foreground italic">(This is where it got emotional)</span>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                Reddit filled with posts that weren't angry — they were <span className="text-foreground font-medium">sad</span>.
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {[
                  "I can't study without it anymore.",
                  "I feel like I lost a friend.",
                  "There was no other site like this.",
                  "I met people there who carried me through exams.",
                ].map((quote, index) => (
                  <Card key={index} className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                    <CardContent className="p-5">
                      <p className="text-foreground italic">"{quote}"</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>Some users had used Studyverse through:</p>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {["Final exams", "Medical school", "Grad school", "Remote work", "Depression and burnout"].map((item, index) => (
                    <div key={index} className="px-4 py-3 rounded-lg bg-muted text-center text-sm font-medium">
                      {item}
                    </div>
                  ))}
                </div>

                <p>
                  People even tried to recreate it manually — pairing Discord calls with ambient sound websites just to approximate the feeling.
                </p>

                <div className="flex flex-wrap gap-3">
                  <Link 
                    to="/study-with-strangers/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Study With Strangers
                  </Link>
                  <Link 
                    to="/blog/benefits-of-online-learning-community/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Benefits of Online Learning Community
                  </Link>
                </div>

                <Card className="border-primary/30 bg-primary/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      A small "Studyverse reunion" Discord popped up so people could find each other again.
                    </p>
                    <p className="text-muted-foreground mt-2 italic">
                      That alone says everything.
                    </p>
                  </CardContent>
                </Card>

                {/* Mobile App Screenshot */}
                <div className="mt-8 max-w-sm mx-auto">
                  <img 
                    src={studyverseMobileApp} 
                    alt="Studyverse mobile app showing focus sessions with friends" 
                    className="w-full rounded-xl shadow-lg border border-border/30"
                    loading="lazy"
                  />
                  <p className="text-center text-sm text-muted-foreground mt-3 italic">
                    Studyverse's mobile app — "Boost focus with friends" was more than a tagline.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why It Probably Failed */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-muted">
                  <TrendingDown className="w-6 h-6 text-muted-foreground" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Why It Probably Failed</h2>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                No official reason was given, but long-time users and former contributors noticed patterns:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {[
                  "Development slowed dramatically",
                  "Bugs went unfixed for long periods",
                  "Moderation issues appeared",
                  "Founder activity went quiet",
                  "No clear monetization path",
                ].map((item, index) => (
                  <Card key={index} className="border-border/50">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-muted-foreground" />
                      <p className="text-muted-foreground">{item}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  Some Reddit users claimed the domain itself lapsed due to ownership issues tied to another company.
                </p>
                <p>
                  Others suggested the product was simply too ambitious for a small team without sustainable revenue.
                </p>

                <div className="flex flex-wrap gap-3 mb-6">
                  <Link 
                    to="/productivity-tool/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Productivity Tools
                  </Link>
                  <Link 
                    to="/blog/apps-boost-productivity-college/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Apps to Boost Productivity
                  </Link>
                </div>

                <Card className="border-muted bg-muted/50">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      A product can be loved — and still disappear.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* What Was Left After */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">What Was Left After</h2>
              <p className="text-4xl font-bold text-muted-foreground mb-4">Nothing.</p>
              <p className="text-lg text-muted-foreground">
                No archive. No read-only mode. No export.
              </p>
              <p className="text-lg text-muted-foreground mt-4 italic">
                Just memories and screenshots.
              </p>
              <p className="text-muted-foreground mt-6">
                That's rare, and honestly, a little brutal.
              </p>

              {/* Cozy Fireplace Image */}
              <div className="mt-10">
                <img 
                  src={studyverseCozyFireplace} 
                  alt="Studyverse cozy library room with fireplace and rain on windows" 
                  className="w-full max-w-2xl mx-auto rounded-xl shadow-lg border border-border/30"
                  loading="lazy"
                />
                <p className="text-sm text-muted-foreground mt-3 italic">
                  The cozy library with rain and fireplace — screenshots like these are all that remain.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Where People Went Next */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Search className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Where People Went Next</h2>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                After Studyverse shut down, people scattered across multiple platforms. Most mentioned alternatives:
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                {alternatives.map((alt, index) => (
                  <Link 
                    key={index}
                    to={alt.url}
                    className="group"
                  >
                    <Card className="h-full border-border/50 hover:border-primary/30 transition-all hover:shadow-lg">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                            {alt.name}
                          </h3>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <p className="text-sm text-muted-foreground">{alt.description}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                <CardContent className="p-6">
                  <p className="text-lg text-muted-foreground">
                    Each replaced a piece of Studyverse — but never the whole thing.
                  </p>
                  <p className="text-muted-foreground mt-3 italic">
                    Which brings us to CSW.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Where CSW Comes In */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/20">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">Where CSW Comes In</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  CSW — <span className="text-foreground font-semibold">Chill Study Work</span> — didn't exist to replace Studyverse.
                </p>
                <p className="text-xl text-foreground font-medium">
                  But it ended up becoming something very close.
                </p>

                <a 
                  href="https://app.csw.live" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                >
                  Try CSW Now
                  <ExternalLink className="w-4 h-4" />
                </a>

                <p className="font-medium text-foreground mt-8">CSW offers:</p>

                <div className="grid md:grid-cols-2 gap-3">
                  {cswFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary/10">
                      <Target className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                <Card className="border-primary/30 bg-primary/5 mt-8">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl">
                      Most importantly, it understands the same core truth Studyverse did:
                    </p>
                    <p className="text-2xl font-bold text-primary mt-3">
                      People don't just want productivity. They want presence.
                    </p>
                  </CardContent>
                </Card>

                <p>
                  CSW isn't perfect — and early users openly said the interface felt rough compared to Studyverse — but it's <span className="text-foreground font-medium">alive, actively developed, and community-driven</span>.
                </p>
                <p className="font-medium text-foreground">And that matters.</p>

                {/* Internal CSW Links */}
                <div className="mt-8 pt-8 border-t border-border/50">
                  <p className="font-medium text-foreground mb-4">Explore CSW Features:</p>
                  <div className="grid md:grid-cols-3 gap-3">
                    <Link to="/study-stream/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Study Stream
                    </Link>
                    <Link to="/study-timer/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Study Timer
                    </Link>
                    <Link to="/task-management/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Task Management
                    </Link>
                    <Link to="/study-with-me/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Study With Me
                    </Link>
                    <Link to="/group-study/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Group Study
                    </Link>
                    <Link to="/study-with-friends/" className="px-4 py-3 rounded-lg bg-muted hover:bg-muted/80 text-center text-sm font-medium transition-colors hover:text-primary">
                      Study With Friends
                    </Link>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 mt-6">
                  <Link 
                    to="/online-study/focusmate/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    CSW vs Focusmate Review
                  </Link>
                  <Link 
                    to="/online-study/studystream/"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors hover:text-primary"
                  >
                    <ArrowRight className="w-4 h-4" />
                    CSW vs StudyStream Review
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What I Miss — And What I Hope */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">What I Miss — And What I Hope</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p className="text-xl text-foreground">
                  I miss logging into a room where nobody spoke, but everyone cared.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    "The quiet accountability",
                    "The feeling of being seen without being watched",
                    "Studying 'together' without pressure",
                  ].map((item, index) => (
                    <Card key={index} className="border-primary/20">
                      <CardContent className="p-4 flex items-center gap-3">
                        <Heart className="w-4 h-4 text-primary shrink-0" />
                        <p className="text-foreground">{item}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 mt-8">
                  <CardContent className="p-8 text-center">
                    <p className="text-xl text-foreground font-medium mb-4">
                      Studyverse proved something important:
                    </p>
                    <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Productivity isn't about grind culture. It's about shared calm.
                    </p>
                  </CardContent>
                </Card>

                <p className="text-center text-xl mt-8">
                  Platforms like CSW show that this idea didn't die — it just <span className="text-foreground font-medium">moved</span>.
                </p>
                <p className="text-center text-lg italic">
                  And maybe that's the best legacy Studyverse could leave behind.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Related Reading Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 text-center">Related Reading</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/blog/science-behind-study-with-me/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        The Science Behind Study With Me
                      </h3>
                      <p className="text-sm text-muted-foreground">Why studying together works for focus and accountability.</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        How Turning On My Camera Boosted My Productivity
                      </h3>
                      <p className="text-sm text-muted-foreground">The unexpected benefits of camera-on study sessions.</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/blog/benefits-of-online-learning-community/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        Benefits of Online Learning Communities
                      </h3>
                      <p className="text-sm text-muted-foreground">How virtual study groups enhance learning outcomes.</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/blog/top-10-digital-tools-for-academic-success/" className="group">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                        Top 10 Digital Tools for Academic Success
                      </h3>
                      <p className="text-sm text-muted-foreground">Essential tools every student should know about.</p>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Find Your Study Space?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Join CSW and experience the same quiet presence that made Studyverse special — for free.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="gap-2" asChild>
                  <Link to="/studyverse/">
                    Learn About CSW
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="gap-2" asChild>
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Try CSW Now
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StudyVerseHistory;